import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { CountriesProvider } from "@/context/CountriesContext";
import { Layout } from "@/components/Layout";
import { RequireAuth } from "@/components/RequireAuth";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import ProductPage from "@/pages/ProductPage";
import ShopPage, { ShopsPage } from "@/pages/ShopPage";
import CartPage from "@/pages/CartPage";
import Checkout from "@/pages/Checkout";
import OrderDetail, { MyOrders } from "@/pages/Orders";
import { Login, Register, VerifyEmail, ForgotPassword, ResetPassword, AuthCallback } from "@/pages/Auth";
import Account from "@/pages/Account";
import BecomeSeller from "@/pages/BecomeSeller";
import Pricing from "@/pages/Pricing";
import SellerLayout from "@/pages/seller/SellerLayout";
import SellerOverview from "@/pages/seller/SellerOverview";
import SellerProducts from "@/pages/seller/SellerProducts";
import ProductEditor from "@/pages/seller/ProductEditor";
import AiWizard from "@/pages/seller/AiWizard";
import SellerOrders from "@/pages/seller/SellerOrders";
import SellerShop from "@/pages/seller/SellerShop";
import SellerSubscription from "@/pages/seller/SellerSubscription";
import AdminLayout, { AdminOverview, AdminUsers, AdminCountries } from "@/pages/admin/AdminLayout";
import { AdminShops, AdminProducts, AdminOrders, AdminSubscriptions } from "@/pages/admin/AdminPages";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminRates from "@/pages/admin/AdminRates";

const auth = (el, opts = {}) => <RequireAuth {...opts}>{el}</RequireAuth>;

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/recherche" element={<Catalog />} />
        <Route path="/produit/:id" element={<ProductPage />} />
        <Route path="/boutiques" element={<ShopsPage />} />
        <Route path="/boutique/:slug" element={<ShopPage />} />
        <Route path="/panier" element={<CartPage />} />
        <Route path="/commande" element={auth(<Checkout />)} />
        <Route path="/mes-commandes" element={auth(<MyOrders />)} />
        <Route path="/mes-commandes/:id" element={auth(<OrderDetail />)} />
        <Route path="/connexion" element={<Login />} />
        <Route path="/inscription" element={<Register />} />
        <Route path="/verifier-email" element={<VerifyEmail />} />
        <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
        <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
        <Route path="/compte" element={auth(<Account />)} />
        <Route path="/devenir-vendeur" element={<BecomeSeller />} />
        <Route path="/abonnements" element={<Pricing />} />
        <Route path="/vendeur" element={auth(<SellerLayout />, { needShop: true })}>
          <Route index element={<SellerOverview />} />
          <Route path="ia" element={<AiWizard />} />
          <Route path="produits" element={<SellerProducts />} />
          <Route path="produits/nouveau" element={<ProductEditor />} />
          <Route path="produits/:id" element={<ProductEditor />} />
          <Route path="commandes" element={<SellerOrders />} />
          <Route path="boutique" element={<SellerShop />} />
          <Route path="abonnement" element={<SellerSubscription />} />
        </Route>
        <Route path="/admin" element={auth(<AdminLayout />, { role: "admin" })}>
          <Route index element={<AdminOverview />} />
          <Route path="boutiques" element={<AdminShops />} />
          <Route path="produits" element={<AdminProducts />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="commandes" element={<AdminOrders />} />
          <Route path="abonnements" element={<AdminSubscriptions />} />
          <Route path="utilisateurs" element={<AdminUsers />} />
          <Route path="pays" element={<AdminCountries />} />
          <Route path="taux" element={<AdminRates />} />
        </Route>
        <Route path="*" element={<p className="p-20 text-center text-gray-500">Page introuvable.</p>} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <CountriesProvider>
      <AuthProvider>
        <CartProvider>
          <AppRouter />
          <Toaster richColors position="top-center" />
        </CartProvider>
      </AuthProvider>
      </CountriesProvider>
    </BrowserRouter>
  );
}
